import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
import { GetCategoryGamesDto } from '@/api/request/categories/get-category-games.dto';
import { CategoryGameDto } from '@/api/response/categories/category-game.dto';
import { SuccessDto } from '@/api/response/success.dto';
import { AddCategoryGamesDto } from '@/api/request/categories/add-category-games.dto';
import { DeleteCategoryGamesDto } from '@/api/request/categories/delete-category-games.dto';
import { SetCategoryGamesDto } from '@/api/request/categories/set-category-games.dto';
import { SetGameCategoriesDto } from '@/api/request/categories/set-game-categories.dto';
import { GetLocalesCategoryGameDto } from '@/api/request/categories/get-locales-category-game.dto';
import { LocalePositionDto } from '@/api/request/categories/locale-position.dto';
import { CategoryPositionDto } from '@/api/request/categories/category-position.dto';
import {
  ImportGamesToCategoryDto,
  ImportGamesToCategoryResponse,
} from '@/api/request/categories/import-games-to-category.dto';
import { TotalResponse } from '@/api/response/total.response';
import { download } from '@/lib/download';

export class CategoryGamesApi {
  constructor(public readonly httpRequest: BaseHttpRequest) {}

  public getCategoryGames(
    dto: GetCategoryGamesDto,
  ): CancelablePromise<CategoryGameDto[]> {
    const additionalFilters: string | undefined =
      dto.additionalFilters !== undefined && dto.additionalFilters.length > 0
        ? dto.additionalFilters.join(',')
        : undefined;

    const dateRange: string | undefined =
      dto.dateRange !== undefined && dto.dateRange.length === 2
        ? dto.dateRange.join(',')
        : undefined;

    return this.httpRequest.request({
      method: 'GET',
      url: `/category-games/list`,
      query: { ...dto, additionalFilters, dateRange },
    });
  }

  public getTotalCategoryGames(
    dto: GetCategoryGamesDto,
  ): CancelablePromise<TotalResponse> {
    const additionalFilters: string | undefined =
      dto.additionalFilters !== undefined && dto.additionalFilters.length > 0
        ? dto.additionalFilters.join(',')
        : undefined;

    const dateRange: string | undefined =
      dto.dateRange !== undefined && dto.dateRange.length === 2
        ? dto.dateRange.join(',')
        : undefined;

    return this.httpRequest.request({
      method: 'GET',
      url: `/category-games/total`,
      query: { ...dto, additionalFilters, dateRange },
    });
  }

  public getCategoryGamesNotSelected(
    dto: GetCategoryGamesDto,
  ): CancelablePromise<CategoryGameDto[]> {
    return this.httpRequest.request({
      method: 'GET',
      url: `/category-games/not-selected`,
      query: dto,
    });
  }

  public getLocalesPositionsByCategoryGame(
    dto: GetLocalesCategoryGameDto,
  ): CancelablePromise<LocalePositionDto[]> {
    return this.httpRequest.request({
      method: 'GET',
      url: `/category-games/locales-positions-category-game`,
      query: dto,
    });
  }

  public getCategoriesByGame(
    gameId: string,
    localeId: number,
  ): CancelablePromise<CategoryPositionDto[]> {
    return this.httpRequest.request({
      method: 'GET',
      url: `/category-games/categories-by-game`,
      query: { gameId: encodeURIComponent(gameId), localeId },
    });
  }

  public addGameToCategory(
    dto: AddCategoryGamesDto,
  ): CancelablePromise<SuccessDto> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/category-games/add',
      mediaType: 'application/json',
      body: dto,
    });
  }

  public deleteGameFromCategory(
    dto: DeleteCategoryGamesDto,
  ): CancelablePromise<SuccessDto> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/category-games/delete',
      mediaType: 'application/json',
      body: dto,
    });
  }

  public setGamesPositionByCategory(
    dto: SetCategoryGamesDto,
  ): CancelablePromise<SuccessDto> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/category-games/position',
      mediaType: 'application/json',
      body: dto,
    });
  }

  public setGamePositionsForCategories(
    dto: SetGameCategoriesDto,
  ): CancelablePromise<SuccessDto> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/category-games/game-categories-position',
      mediaType: 'application/json',
      body: dto,
    });
  }

  public importGamesToCategory(
    formData: ImportGamesToCategoryDto,
  ): CancelablePromise<ImportGamesToCategoryResponse> {
    return this.httpRequest.request<ImportGamesToCategoryResponse>({
      method: 'POST',
      url: '/category-games/import',
      formData,
      mediaType: 'multipart/form-data',
    });
  }

  async exportGamesFromCategory(
    dto: GetCategoryGamesDto,
    fileName: string,
  ): Promise<void> {
    const additionalFilters: string | undefined =
      dto.additionalFilters !== undefined && dto.additionalFilters.length > 0
        ? dto.additionalFilters.join(',')
        : undefined;

    const dateRange: string | undefined =
      dto.dateRange !== undefined && dto.dateRange.length === 2
        ? dto.dateRange.join(',')
        : undefined;

    const csvText = await this.httpRequest.request<string>({
      method: 'GET',
      url: `/category-games/export`,
      query: { ...dto, additionalFilters, dateRange },
    });

    download(csvText, fileName);
  }
}
